import { useCallback, useState } from 'react'

// RN28/RN30/RN76: the "dirty batch" of the General block. Lives in the CONTAINER
// (FabricationOrderDetailDialog), not in GeneralBlock itself, so it survives switching to another
// block (S95) even though GeneralBlock stops rendering while another block is active — the
// container never loses this state, only the block's own presentation does.

// The empty values (`null`, `undefined`, `''`) are the same "no value"; dates compare by instant,
// references by id, booleans by truth. Anything else compares as text (`5` and `'5'` are the same
// priority).
const comparable = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'object') return value.id === undefined ? JSON.stringify(value) : `id:${value.id}`
  return String(value)
}

export const sameFieldValue = (value, originalValue) => {
  if (typeof value === 'boolean') return value === !!originalValue
  if (value instanceof Date) {
    const original = originalValue instanceof Date ? originalValue : new Date(originalValue)
    return originalValue !== null && originalValue !== undefined && originalValue !== '' && value.getTime() === original.getTime()
  }
  return comparable(value) === comparable(originalValue)
}

export const useUnsavedChanges = () => {
  const [pending, setPending] = useState({})
  const [snapshot, setSnapshot] = useState({})
  // Kept here for the same reason as `pending`: GeneralBlock unmounts when another block is active.
  // The marker is tied to the header it was read against, so a new header (background reread)
  // makes it stale by itself. `savedBatch` is what was just saved, until the reread lands.
  const [freshMarker, setFreshMarker] = useState({ value: '', forLastModified: '' })
  const [savedBatch, setSavedBatch] = useState({ header: {}, headerValues: {}, sheet: {}, sheetValues: {} })
  // The installation's extended columns, handed over by GeneralBlock: the prompt names the touched
  // fields by their column name, and only the block reads them.
  const [extendedColumns, setExtendedColumns] = useState([])
  const [pendingAction, setPendingAction] = useState(null)
  // POR QUÉ se está pidiendo abandonar: cerrar/saltar de orden, o abrir algo que TOMA LA VENTANA
  // (workflow, balanced). El aviso dice cosas distintas en cada caso —al cerrar se pierde lo
  // escrito, al abrir el workflow lo normal es guardar y seguir—, así que el motivo tiene que
  // viajar con la acción y no adivinarse en quien pinta.
  const [pendingReason, setPendingReason] = useState('')

  const hasChanges = Object.keys(pending).length > 0

  // Records a field edit; the FIRST time a field is touched its loaded value is kept in `snapshot`
  // so "Discard" (S89) can restore it without re-fetching anything.
  // Writing the loaded value back is not a change: the field leaves the batch.
  const setField = useCallback((field, value, originalValue) => {
    if (sameFieldValue(value, originalValue)) {
      const without = (previous) => {
        if (!(field in previous)) return previous
        const next = { ...previous }
        delete next[field]
        return next
      }
      setPending(without)
      setSnapshot(without)
      return
    }
    setSnapshot((prevSnapshot) => (field in prevSnapshot ? prevSnapshot : { ...prevSnapshot, [field]: originalValue }))
    setPending((prevPending) => ({ ...prevPending, [field]: value }))
  }, [])

  const discard = useCallback(() => {
    setPending({})
    setSnapshot({})
  }, [])

  // RN76: guards any action that would give up the window (close, jump to another order, open
  // Workflow/Balanced). With nothing pending it runs the action right away (S99); otherwise it is
  // stashed for UnsavedChangesPrompt to resolve (save/discard/keep editing).
  const guard = useCallback((action, reason = 'close') => {
    if (Object.keys(pending).length === 0) {
      action()
      return
    }
    setPendingAction(() => action)
    setPendingReason(reason)
  }, [pending])

  const resolveKeepEditing = useCallback(() => setPendingAction(null), [])

  // Both ways out of the prompt end the same: the batch is dropped (discarded, or already persisted by
  // the save) and the guarded action goes on.
  // S97: for "saved" the caller is responsible for actually saving (GeneralBlock owns
  // validation/concurrency) and must only call this once the save has succeeded.
  const resolveAndContinue = useCallback(() => {
    const action = pendingAction
    setPending({})
    setSnapshot({})
    setPendingAction(null)
    if (action) action()
  }, [pendingAction])

  return {
    pending,
    snapshot,
    hasChanges,
    freshMarker,
    setFreshMarker,
    savedBatch,
    setSavedBatch,
    extendedColumns,
    setExtendedColumns,
    setField,
    discard,
    guard,
    isPromptOpen: !!pendingAction,
    pendingReason,
    resolveKeepEditing,
    resolveDiscardAndContinue: resolveAndContinue,
    resolveSavedAndContinue: resolveAndContinue
  }
}
